import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect } from '../FormInputs';
import Select from 'react-select';

interface SentimentAnalysisSubgroupProps {
  form: any;
  isDisabled?: boolean;
}

export const SentimentAnalysisSubgroup: React.FC<SentimentAnalysisSubgroupProps> = ({ form, isDisabled = false }) => {
  const { t } = useTranslation();

  const aspectBasedSentimentOptions = [
    { label: t('Sentiment towards a topic or narrative'), value: 'SENTIMENT_STATEMENT' },
    { label: t('Sentiment towards person'), value: 'SENTIMENT_PERSON' },
    { label: t('Sentiment towards organization'), value: 'SENTIMENT_ORGANIZATION' },
    { label: t('Sentiment towards country'), value: 'SENTIMENT_COUNTRY' },
    { label: t('Custom sentiment option'), value: 'SENTIMENT_CUSTOM' },
  ];

  const sentimentAnalysisTitle = {
    'SENTIMENT_STATEMENT': {
      title: t('Topic/Narrative'),
      hint: t('Give a short name of a topic, narrative or opinion to measure reaction to'),
      placeholder: t('E.g. ceasefire in Gaza')
    },
    'SENTIMENT_PERSON': {
      title: t('Person'),
      hint: t('Write a name and minimum necessary content that helps identify a person in content'),
      placeholder: t('E.g. Vladimir Putin, leader of PWC Wagner'),
    },
    'SENTIMENT_ORGANIZATION': {
      title: t('Organization'),
      hint: t('Write a name of an organization'),
      placeholder: t('E.g. UN, EU'),
    },
    'SENTIMENT_COUNTRY': {
      title: t('Country'),
      hint: t('Write a name of a country'),
      placeholder: t('E.g. Russia, USA'),
    },
    'SENTIMENT_CUSTOM': {
      title: t('Custom sentiment option'),
      hint: t('Write a custom keyword or phrase to measure sentiment towards'),
      placeholder: t('E.g. "I love my country"'),
    },
  };

  return (
    <div className="form-element-group">
      <form.Subscribe
        selector={(state: any) => ({
          isAspectBasedSentimentEnabled:
            state.values.isAspectBasedSentimentEnabled,
        })}
        children={(isAspectBasedSentimentEnabled: boolean) => (
          <form.Field
            id="aspectBasedSentimentObject"
            name="aspectBasedSentimentObject"
            validators={{
              onBlur: ({ value }: any) => {
                if (isAspectBasedSentimentEnabled && value === null) {
                  return t('This field is required');
                }
                return undefined;
              },
              onChange: ({ value }: any) => {
                if (isAspectBasedSentimentEnabled && value === null) {
                  return t('This field is required');
                }
                return undefined;
              },
            }}
            children={(field: any) => {
              const hasError = field.state.meta.errors?.length > 0;
              const errorMessage = field.state.meta.errors?.[0];

              return (
                <div className="form-element" id="aspectBasedSentimentObject">
                  <label htmlFor="aspectBasedSentimentObject">
                    {t('Sentiment analysis')}
                  </label>

                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    options={aspectBasedSentimentOptions}
                    value={field.state.value}
                    onChange={(value: any) => field.handleChange(value)}
                    onBlur={field.handleBlur}
                    placeholder={t('Choose context for sentiment analysis')}
                    isClearable={true}
                    isSearchable={true}
                    isDisabled={isDisabled}
                    name="aspectBasedSentimentObject"
                  />
                  {hasError && (
                    <div className="error-message">{errorMessage}</div>
                  )}
                </div>
              );
            }}
          />
        )}
      />

      <form.Subscribe
        selector={(state: any) => state.values.aspectBasedSentimentObject}
        children={(aspectBasedSentimentObjectValue: any) => {
          const selectedValue = aspectBasedSentimentObjectValue?.value;
          const titleData =
            sentimentAnalysisTitle[
              selectedValue as keyof typeof sentimentAnalysisTitle
            ];

          return (
            aspectBasedSentimentObjectValue && (
              <form.Field
                name="aspectBasedSentimentSubject"
                validators={{
                  onBlur: ({ value }: any) => {
                    const aspectBasedSentimentObject = form.getFieldValue(
                      'aspectBasedSentimentObject',
                    );
                    if (aspectBasedSentimentObject !== null && !value?.trim()) {
                      return t(
                        'You must provide a subject for aspect based sentiment analysis',
                      );
                    }
                    return undefined;
                  },
                }}
                children={(subjectField: any) => {
                  const hasError = subjectField.state.meta.errors?.length > 0;
                  const errorMessage = subjectField.state.meta.errors?.[0];

                  return (
                    <div
                      className={
                        hasError ? 'form-element error' : 'form-element'
                      }
                      id="aspectBasedSentimentSubject"
                    >
                      {titleData?.title && (
                        <label htmlFor="aspectBasedSentimentSubject">
                          {titleData.title}*
                          {titleData?.hint && (
                            <span className="sub-label"> {titleData.hint}</span>
                          )}
                        </label>
                      )}
                      <input
                        id="aspectBasedSentimentSubject"
                        name="aspectBasedSentimentSubject"
                        type="text"
                        value={subjectField.state.value || ''}
                        onChange={(e) =>
                          subjectField.handleChange(e.target.value)
                        }
                        onBlur={subjectField.handleBlur}
                        placeholder={titleData?.placeholder}
                        required
                        disabled={isDisabled}
                        className=""
                      />
                      {hasError && (
                        <div className="error-message">{errorMessage}</div>
                      )}
                    </div>
                  );
                }}
              />
            )
          );
        }}
      />
    </div>
  );
};